// Trees — Basic vs Optimized code for every "Beginner Problems" drill listed in the Trees
// Explanation modal. Same shape as linkedListDrills.js: concise steps + an interactive "watch
// it happen" visualizer per version, using ApproachVisualizer's tree-rendering support. Three
// drills cross-reference the curated problems instead of duplicating content.

const treeDrills = [
  {
    id: 'tree-traversal-basics',
    title: 'Preorder Traversal: Recursive vs Iterative',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Recursive',
        explanation: 'Visit the current node, then recursively traverse the left subtree, then the right. This is the most natural way to write it — but every recursive call adds a frame to Java\'s own call stack, the same trade-off covered in the Stack pattern.',
        steps: [
          'If the node is null, there\'s nothing to do — the base case.',
          'Otherwise, visit (record) the current node\'s value first.',
          'Recursively traverse the left subtree.',
          'Recursively traverse the right subtree.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 1, left: { val: 2, left: { val: 4, left: null, right: null }, right: { val: 5, left: null, right: null } }, right: { val: 3, left: null, right: null } } },
          varNames: [],
          steps: [
            { highlights: { tree: [1] }, visited: [], note: 'Visit 1 first (preorder: node before children).' },
            { highlights: { tree: [2] }, visited: [1], note: 'Recurse left: visit 2.' },
            { highlights: { tree: [4] }, visited: [1, 2], note: 'Recurse left again: visit 4 (a leaf).' },
            { highlights: { tree: [5] }, visited: [1, 2, 4], note: 'Back up, recurse right of 2: visit 5.' },
            { highlights: { tree: [3] }, visited: [1, 2, 4, 5], note: 'Back up to 1, recurse right: visit 3. Final order: 1, 2, 4, 5, 3.' },
          ],
        },
        code:
`void preorderRecursive(TreeNode node, List<Integer> result) {
    if (node == null) return;
    result.add(node.val);
    preorderRecursive(node.left, result);
    preorderRecursive(node.right, result);
}`,
        complexity: { time: 'O(n)', space: 'O(h) — call-stack depth, h = tree height' },
      },
      {
        name: 'Optimized — Iterative With an Explicit Stack',
        explanation: 'Push nodes onto your OWN stack instead of relying on Java\'s call stack. Push the right child before the left child, so the left one gets popped (and visited) first — reproducing the exact same order as the recursive version, without any recursion at all.',
        steps: [
          'Push the root onto a stack.',
          'While the stack isn\'t empty, pop a node and visit it.',
          'Push its right child first, then its left child (so left is popped next).',
          'Repeat until the stack is empty.',
        ],
        code:
`void preorderIterative(TreeNode root, List<Integer> result) {
    if (root == null) return;
    Deque<TreeNode> stack = new ArrayDeque<>();
    stack.push(root);
    while (!stack.isEmpty()) {
        TreeNode node = stack.pop();
        result.add(node.val);
        if (node.right != null) stack.push(node.right);   // push right FIRST
        if (node.left != null) stack.push(node.left);      // so left pops first
    }
}`,
        complexity: { time: 'O(n)', space: 'O(h) — your own explicit stack instead of Java\'s' },
      },
    ],
  },
  {
    id: 'count-nodes-in-tree',
    title: 'Count the Total Number of Nodes in a Tree',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Level-Order With a Queue',
        explanation: 'Walk the tree level by level using a queue, incrementing a counter for every node dequeued. Correct, but the queue can hold up to an entire level\'s worth of nodes at once — for a wide tree, that can mean O(n) extra space.',
        steps: [
          'Start a queue containing just the root, and a counter at 0.',
          'While the queue isn\'t empty, dequeue a node and increment the counter.',
          'Enqueue that node\'s children (if they exist).',
          'Return the counter once the queue empties.',
        ],
        code:
`int countNodesBasic(TreeNode root) {
    if (root == null) return 0;
    Queue<TreeNode> queue = new LinkedList<>();
    queue.add(root);
    int count = 0;
    while (!queue.isEmpty()) {
        TreeNode node = queue.poll();
        count++;
        if (node.left != null) queue.add(node.left);
        if (node.right != null) queue.add(node.right);
    }
    return count;
}`,
        complexity: { time: 'O(n)', space: 'O(w) — w = the tree\'s widest level, up to O(n)' },
      },
      {
        name: 'Optimized — Recursive Count',
        explanation: 'A tree\'s node count is always 1 (itself) plus however many nodes are in its left subtree plus however many are in its right subtree. No queue needed — the recursion IS the traversal, and its space cost is bounded by the tree\'s height rather than its width.',
        steps: [
          'If the node is null, it contributes 0 to the count — the base case.',
          'Otherwise, the count is 1 plus the count of the left subtree plus the count of the right subtree.',
          'Recurse on both children to get their counts.',
          'Space cost is bounded by height, not width — better for wide, shallow trees.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 5, left: { val: 3, left: { val: 1, left: null, right: null }, right: null }, right: { val: 8, left: null, right: null } } },
          varNames: [],
          steps: [
            { highlights: { tree: [1] }, note: 'count(1) = 1 + count(null) + count(null) = 1 (a leaf).' },
            { highlights: { tree: [3] }, note: 'count(3) = 1 + count(1) + count(null) = 1 + 1 + 0 = 2.' },
            { highlights: { tree: [8] }, note: 'count(8) = 1 + count(null) + count(null) = 1 (a leaf).' },
            { highlights: { tree: [5] }, note: 'count(5) = 1 + count(3) + count(8) = 1 + 2 + 1 = 4. Total nodes: 4.' },
          ],
        },
        code:
`int countNodes(TreeNode node) {
    if (node == null) return 0;
    return 1 + countNodes(node.left) + countNodes(node.right);
}`,
        complexity: { time: 'O(n)', space: 'O(h) — call-stack depth, h = tree height' },
      },
    ],
  },
  {
    id: 'search-in-bst',
    title: 'Search for a Value in a Binary Search Tree',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Check Every Node',
        explanation: 'Traverse the entire tree (in any order) and compare every node\'s value to the target. Correct on ANY binary tree, sorted or not — but it never uses the BST ordering property at all.',
        steps: [
          'Visit every node using any traversal (preorder works fine).',
          'Compare each node\'s value to the target.',
          'Return true (or the node) the instant a match is found.',
          'In the worst case, every node is checked — O(n), ignoring the BST\'s structure entirely.',
        ],
        code:
`boolean searchBasic(TreeNode node, int target) {
    if (node == null) return false;
    if (node.val == target) return true;
    return searchBasic(node.left, target) || searchBasic(node.right, target);
}`,
        complexity: { time: 'O(n)', space: 'O(h)' },
      },
      {
        name: 'Optimized — Follow the BST Property Down One Path',
        explanation: 'In a BST, every node\'s left subtree holds only smaller values and its right subtree holds only bigger ones. That means at every node, comparing the target to the current value rules out an ENTIRE subtree — the same halving idea as binary search, just walking down a tree instead of narrowing an array range.',
        steps: [
          'Compare the target to the current node\'s value.',
          'If they\'re equal, found it.',
          'If the target is smaller, the answer (if it exists) must be in the left subtree — recurse there.',
          'If the target is bigger, recurse right. Only one path from root to a leaf is ever explored.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 8, left: { val: 3, left: { val: 1, left: null, right: null }, right: { val: 6, left: null, right: null } }, right: { val: 10, left: null, right: { val: 14, left: null, right: null } } } },
          varNames: [],
          steps: [
            { highlights: { tree: [8] }, note: '6 < 8 — go left.' },
            { highlights: { tree: [3] }, note: '6 > 3 — go right.' },
            { highlights: { tree: [6] }, note: '6 = 6 — found, after only 3 comparisons on a 6-node tree.' },
          ],
        },
        code:
`boolean search(TreeNode node, int target) {
    if (node == null) return false;
    if (node.val == target) return true;
    return (target < node.val) ? search(node.left, target) : search(node.right, target);
}`,
        complexity: { time: 'O(h) — O(log n) if balanced, O(n) if degenerate', space: 'O(h)' },
      },
    ],
  },
  {
    id: 'level-order-traversal-drill',
    title: 'Binary Tree Level Order Traversal',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Binary Tree Level Order Traversal\" there for the complete step-by-step and both code versions. You " +
      "can also try Level-Order (BFS) yourself in the \"Traversal Types\" mode of the Interactive Practice playground.",
  },
  {
    id: 'binary-tree-right-side-view',
    title: 'Binary Tree Right Side View',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Level-Order, Keep the Last Node of Each Level',
        explanation: 'Do a standard level-order (BFS) traversal, but only remember the LAST node processed at each level — that\'s the one visible standing to the right of the tree. Correct and easy to reason about.',
        steps: [
          'Do a level-order traversal, processing one full level (using the queue\'s current size) at a time.',
          'Within each level, walk through every node at that level.',
          'The last node visited in that level is the one visible from the right — record it.',
          'Move to the next level and repeat.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 1, left: { val: 2, left: null, right: { val: 5, left: null, right: null } }, right: { val: 3, left: null, right: { val: 4, left: null, right: null } } } },
          varNames: [],
          steps: [
            { highlights: { tree: [1] }, note: 'Level 0: just [1]. Last (and only) node: 1. Record 1.' },
            { highlights: { tree: [2, 3] }, note: 'Level 1: [2, 3] in level order. Last node: 3. Record 3.' },
            { highlights: { tree: [5, 4] }, note: 'Level 2: [5, 4] in level order (5 is 2\'s right child, 4 is 3\'s right child). Last node: 4. Record 4. Right side view: [1, 3, 4].' },
          ],
        },
        code:
`public List<Integer> rightSideViewBasic(TreeNode root) {
    List<Integer> result = new ArrayList<>();
    if (root == null) return result;
    Queue<TreeNode> queue = new LinkedList<>();
    queue.add(root);

    while (!queue.isEmpty()) {
        int levelSize = queue.size();
        TreeNode lastInLevel = null;
        for (int i = 0; i < levelSize; i++) {
            TreeNode node = queue.poll();
            lastInLevel = node;
            if (node.left != null) queue.add(node.left);
            if (node.right != null) queue.add(node.right);
        }
        result.add(lastInLevel.val);
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(w) — the queue, up to a full level\'s width' },
      },
      {
        name: 'Optimized — DFS, Right Child First',
        explanation: 'Do a depth-first traversal that visits the RIGHT child before the left one, tracking depth as you go. The very first node reached at each depth is guaranteed to be the rightmost one at that depth — record a depth\'s answer only the first time it\'s reached, and every later (necessarily more-left) node at that depth gets correctly ignored.',
        steps: [
          'Recurse depth-first, visiting the right child before the left child.',
          'Track the current depth as the recursion goes deeper.',
          'The first time a given depth is reached, record that node\'s value — it\'s the rightmost one there.',
          'Any later node reached at an already-recorded depth is further left, and is skipped.',
        ],
        code:
`public List<Integer> rightSideView(TreeNode root) {
    List<Integer> result = new ArrayList<>();
    dfs(root, 0, result);
    return result;
}

private void dfs(TreeNode node, int depth, List<Integer> result) {
    if (node == null) return;
    if (depth == result.size()) {   // first time reaching this depth
        result.add(node.val);
    }
    dfs(node.right, depth + 1, result);   // right FIRST
    dfs(node.left, depth + 1, result);
}`,
        complexity: { time: 'O(n)', space: 'O(h) — call-stack depth instead of a full level\'s width' },
      },
    ],
  },
  {
    id: 'lowest-common-ancestor-bst-drill',
    title: 'Lowest Common Ancestor of a BST',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Lowest Common Ancestor of a BST\" there for the complete step-by-step and both code versions.",
  },
  {
    id: 'validate-bst-drill',
    title: 'Validate a Binary Search Tree',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Validate Binary Search Tree\" there for the complete step-by-step and both code versions.",
  },
  {
    id: 'construct-tree-from-preorder-inorder',
    title: 'Construct a Binary Tree From Preorder and Inorder Traversals',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Linear Search for the Root in Inorder, Each Call',
        explanation: 'The first element of a preorder sequence is always the root. Find that same value\'s position in the inorder sequence by scanning for it — everything before that position is the left subtree, everything after is the right subtree. Recurse on both. Correct, but each recursive call re-scans a chunk of the inorder array from scratch.',
        steps: [
          'Take the first element of the current preorder slice — that\'s this subtree\'s root.',
          'Scan the current inorder slice to find that value\'s position.',
          'Everything to its left in inorder is the left subtree; everything to its right is the right subtree.',
          'Recurse on both slices — but each call\'s scan costs O(n) in the worst case, and there are O(n) calls, so O(n²) total.',
        ],
        code:
`public TreeNode buildTreeBasic(int[] preorder, int[] inorder) {
    if (preorder.length == 0) return null;
    int rootVal = preorder[0];
    TreeNode root = new TreeNode(rootVal);

    int mid = 0;
    for (int i = 0; i < inorder.length; i++) {
        if (inorder[i] == rootVal) { mid = i; break; }   // linear scan, every call
    }

    root.left = buildTreeBasic(
        Arrays.copyOfRange(preorder, 1, mid + 1),
        Arrays.copyOfRange(inorder, 0, mid));
    root.right = buildTreeBasic(
        Arrays.copyOfRange(preorder, mid + 1, preorder.length),
        Arrays.copyOfRange(inorder, mid + 1, inorder.length));
    return root;
}`,
        complexity: { time: 'O(n²)', space: 'O(n²) — the repeated array slicing' },
      },
      {
        name: 'Optimized — HashMap of Value → Index, Built Once',
        explanation: 'Precompute a HashMap from value to its index in the inorder array, ONCE, before any recursion starts. Every recursive call then finds the root\'s split position in O(1) instead of rescanning — and passing index ranges instead of copying array slices avoids the repeated allocation too.',
        steps: [
          'Build a HashMap of value → index across the entire inorder array, up front.',
          'Track a moving pointer into the preorder array (each call consumes exactly one more root).',
          'For each call, look up the root\'s inorder index in O(1), then recurse on the correct index RANGES (no copying).',
          'Build the left subtree first (since preorder visits left before right), then the right.',
        ],
        visual: {
          arrays: [{ label: 'preorder', values: [3, 9, 20, 15, 7] }, { label: 'inorder', values: [9, 3, 15, 20, 7] }],
          tree: { label: 'tree being built', value: null },
          varNames: [],
          steps: [
            { highlights: { preorder: [0], inorder: [1] }, note: 'Root of the whole tree: preorder[0] = 3. Its inorder index is 1 — indices [0,0] (just "9") are the left subtree, indices [2,4] ("15,20,7") are the right subtree.' },
            { snapshots: { tree: { val: 3, left: { val: 9, left: null, right: null }, right: null } }, highlights: { preorder: [1], inorder: [0] }, note: 'Left subtree\'s root: preorder[1] = 9. Inorder index 0, with no elements on either side — it\'s a leaf.' },
            { highlights: { preorder: [2], inorder: [3] }, note: 'Right subtree\'s root: preorder[2] = 20. Its inorder index (within [15,20,7]) splits into left=[15], right=[7].' },
            { snapshots: { tree: { val: 3, left: { val: 9, left: null, right: null }, right: { val: 20, left: { val: 15, left: null, right: null }, right: { val: 7, left: null, right: null } } } }, note: 'Attach 15 as 20\'s left child and 7 as 20\'s right child. Tree fully built — every lookup was O(1) via the HashMap.' },
          ],
        },
        code:
`public TreeNode buildTree(int[] preorder, int[] inorder) {
    Map<Integer, Integer> indexOf = new HashMap<>();
    for (int i = 0; i < inorder.length; i++) indexOf.put(inorder[i], i);

    int[] preIndex = { 0 };   // shared mutable pointer across recursive calls
    return build(preorder, indexOf, preIndex, 0, inorder.length - 1);
}

private TreeNode build(int[] preorder, Map<Integer, Integer> indexOf, int[] preIndex, int left, int right) {
    if (left > right) return null;

    int rootVal = preorder[preIndex[0]++];
    TreeNode root = new TreeNode(rootVal);
    int mid = indexOf.get(rootVal);

    root.left = build(preorder, indexOf, preIndex, left, mid - 1);
    root.right = build(preorder, indexOf, preIndex, mid + 1, right);
    return root;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — the HashMap, plus O(h) recursion depth' },
      },
    ],
  },
  {
    id: 'serialize-deserialize-binary-tree',
    title: 'Serialize and Deserialize a Binary Tree',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Level-Order (BFS) With Null Markers',
        explanation: 'Walk the tree level by level with a queue, writing down every value — including an explicit marker for missing children — so the exact shape can be rebuilt later. Deserializing replays the same queue-driven process in reverse.',
        steps: [
          'BFS the tree with a queue, appending each node\'s value (or a "#" marker for null) to the output.',
          'Even null children get enqueued conceptually so their "#" marker appears in the right position.',
          'To deserialize, read tokens back in the same level-order sequence, using a queue of "parents still needing children assigned."',
          'This works, but the queue-driven bookkeeping (on both ends) is more moving parts than necessary.',
        ],
        code:
`public String serializeBasic(TreeNode root) {
    StringBuilder sb = new StringBuilder();
    Queue<TreeNode> queue = new LinkedList<>();
    queue.add(root);
    while (!queue.isEmpty()) {
        TreeNode node = queue.poll();
        if (node == null) { sb.append("#,"); continue; }
        sb.append(node.val).append(",");
        queue.add(node.left);
        queue.add(node.right);
    }
    return sb.toString();
}`,
        complexity: { time: 'O(n)', space: 'O(n) — the queue and the output string' },
      },
      {
        name: 'Optimized — Preorder (DFS) With Null Markers',
        explanation: 'Write the tree out in preorder, using an explicit marker for every null child. Because preorder always visits a node BEFORE its children, reading the tokens back in the same order — recursively, one token at a time — rebuilds the exact same tree shape with no queue bookkeeping at all.',
        steps: [
          'Serialize with a simple preorder DFS: write the current value, then recurse left, then right — writing "#" for any null.',
          'To deserialize, read tokens one at a time from the front.',
          'If the next token is "#", that subtree is null — consume it and return.',
          'Otherwise, create a node with that value, then recursively build its left subtree, then its right subtree, consuming tokens as you go.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 1, left: { val: 2, left: null, right: null }, right: { val: 3, left: { val: 4, left: null, right: null }, right: { val: 5, left: null, right: null } } } },
          arrays: [{ label: 'serialized so far', values: [] }],
          varNames: [],
          steps: [
            { highlights: { tree: [1] }, snapshots: { 'serialized so far': [1] }, note: 'Visit 1 (preorder): write "1".' },
            { highlights: { tree: [2] }, snapshots: { 'serialized so far': [1, 2, '#', '#'] }, note: 'Visit 2: write "2", then its null left and null right: "#", "#".' },
            { highlights: { tree: [3] }, snapshots: { 'serialized so far': [1, 2, '#', '#', 3] }, note: 'Back to 1\'s right child: visit 3, write "3".' },
            { highlights: { tree: [4] }, snapshots: { 'serialized so far': [1, 2, '#', '#', 3, 4, '#', '#'] }, note: 'Visit 4 (3\'s left child): write "4", "#", "#".' },
            { highlights: { tree: [5] }, snapshots: { 'serialized so far': [1, 2, '#', '#', 3, 4, '#', '#', 5, '#', '#'] }, note: 'Visit 5 (3\'s right child): write "5", "#", "#". Full serialization: 1,2,#,#,3,4,#,#,5,#,# — reading it back in order rebuilds this exact tree.' },
          ],
        },
        code:
`public String serialize(TreeNode node) {
    if (node == null) return "#";
    return node.val + "," + serialize(node.left) + "," + serialize(node.right);
}

public TreeNode deserialize(String data) {
    Deque<String> tokens = new ArrayDeque<>(Arrays.asList(data.split(",")));
    return build(tokens);
}

private TreeNode build(Deque<String> tokens) {
    String token = tokens.poll();
    if (token.equals("#")) return null;
    TreeNode node = new TreeNode(Integer.parseInt(token));
    node.left = build(tokens);
    node.right = build(tokens);
    return node;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — the output string, plus O(h) recursion depth' },
      },
    ],
  },
];

export default treeDrills;
